import intlTelInput from 'intl-tel-input'
import type { Iso2, Iti, UtilsLoader } from 'intl-tel-input'
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import 'intl-tel-input/styles'
import { cn } from '../cn.js'
import { dialCodeFor } from '../countries.js'
import './PhoneField.css'

export interface PhoneFieldProps {
  label: string
  /** E.164, e.g. `+66812345678`. Empty until there is something to report. */
  value: string
  onChange: (e164: string) => void
  /**
   * ISO 3166-1 alpha-2 to open on — the country of residence, normally. Changing
   * it moves the selector only while the field is still empty, so choosing a
   * country after typing a number never rewrites the number.
   */
  country?: string
  /** Names the countries in the dropdown; defaults to English. */
  locale?: string
  /**
   * Submitted name for the national part. Nothing here submits natively, but
   * password managers read `name` as well as `autocomplete` when deciding what
   * a box is for, and an unnamed one is the case their heuristics get wrong.
   */
  name?: string
  hint?: ReactNode
  error?: string | undefined
  required?: boolean
  disabled?: boolean
  /** Shown on blur when the number cannot be a real one for the chosen country. */
  invalidText?: string
  /**
   * Where the ~250 KB validation bundle comes from. Loaded lazily and only
   * needed for formatting and E.164 output; tests pass `null` to skip it.
   */
  loadUtils?: UtilsLoader | null
}

/**
 * The phone number field (PLAN.md §9.5).
 *
 * A bare `<input type="tel">` asks people to guess: `0812345678`, `+66 81 234
 * 5678` and `66812345678` are all reasonable readings of "phone number", and
 * only one of them is what the CRM stores. Guessing wrong is not a validation
 * message — it is an OTP that never arrives.
 *
 * `intl-tel-input` (jackocnr) settles it: the country prefix is picked, not
 * typed, and the national part is formatted as you type against libphonenumber's
 * rules for that country. What leaves this component is always E.164.
 *
 * It is a vanilla-JS widget that owns its DOM, so the `<input>` here is
 * uncontrolled by necessity — React sets the value once on mount and thereafter
 * reads what the widget produces. Re-rendering does not fight it.
 */
/**
 * Runs an `Iti` call that needs the lazily-loaded utils bundle.
 *
 * Those methods throw outright when it has not arrived — `getNumber()` raises
 * "intlTelInput.utils is required" rather than returning empty — and one of
 * them runs on every keystroke and inside a mount effect. Unguarded, a CSP
 * rule or a failed chunk fetch does not degrade the phone field, it throws
 * through React and takes the whole step down. The widget still works without
 * utils; it just stops formatting.
 */
function withoutUtils<T>(call: () => T): T | undefined {
  try {
    return call()
  } catch {
    return undefined
  }
}

/**
 * Forces E.164 shape: a leading `+` and digits, nothing else.
 *
 * `getNumber()` only returns true E.164 for a number libphonenumber can parse
 * as valid. For anything else it hands back the dial code with the raw input
 * appended, spaces and all — so a half-typed Thai number submitted as
 * `+6632 323 23`, which the contract's permissive `/^\+?[\d\s().-]+$/` waves
 * through and the CRM then stores or rejects on its own terms. The widget
 * exists precisely so the server never has to guess at formatting.
 */
function normalise(number: string): string {
  const digits = number.replace(/\D/g, '')
  return digits === '' ? '' : `+${digits}`
}

export function PhoneField({
  label,
  value,
  onChange,
  country,
  locale,
  name,
  hint,
  error,
  required,
  disabled,
  invalidText = 'That does not look like a phone number for the country selected.',
  loadUtils = () => import('intl-tel-input/utils'),
}: PhoneFieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`

  const [invalid, setInvalid] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const itiRef = useRef<Iti | null>(null)
  // What we last handed upstream. The parent echoes it back as `value`, and
  // without this the sync effect below would rewrite the input mid-keystroke.
  const reportedRef = useRef(value)
  // Read through a ref so a new inline `onChange` each render does not tear
  // down and rebuild the widget.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Everything the widget needs at construction time. Deliberately not in the
  // dependency list: re-initialising drops the DOM the user is typing into.
  const initialRef = useRef({ country, locale, loadUtils, value })

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const { country: initialCountry, locale: nameLocale, loadUtils: utils, value: initial } =
      initialRef.current

    const iti = intlTelInput(input, {
      initialCountry: (initialCountry?.toLowerCase() ?? '') as Iso2 | '',
      // The dial code sits outside the text box rather than inside it, so the
      // prefix reads as a property of the field and the national number is the
      // only thing to type.
      separateDialCode: true,
      // No flags. The dial code is the thing being chosen and the only thing
      // that ends up in the number; a flag beside it adds a second, competing
      // identifier — and asks the form to take a position on which territories
      // get one, which is not a question a phone field should be answering.
      showFlags: false,
      countrySearch: true,
      formatAsYouType: true,
      // Rejects characters that cannot appear in a number for the chosen
      // country, which is cheaper than explaining the mistake afterwards.
      strictMode: true,
      countryNameLocale: nameLocale ?? 'en',
      loadUtils: utils,
    })
    itiRef.current = iti
    if (initial) withoutUtils(() => iti.setNumber(initial))

    const report = () => {
      // Composed by hand when utils cannot format it, rather than reporting
      // nothing: a plain `+<dial><digits>` the server can still parse beats a
      // field that silently submits blank.
      let next = withoutUtils(() => iti.getNumber()) ?? ''
      if (!next) {
        const digits = input.value.replace(/\D/g, '')
        const selected = iti.getSelectedCountry()
        next = digits && selected ? `+${selected.dialCode}${digits}` : ''
      }
      reportedRef.current = normalise(next)
      onChangeRef.current(reportedRef.current)

      // Nagging mid-number is wrong: every number is invalid while it is half
      // typed. The warning is raised on blur and only cleared here.
      setInvalid(false)
    }

    const check = () => {
      // Guarded on the bundle rather than on the return value: with utils
      // missing `isValidNumber()` does not throw and does not return null, it
      // returns false — so every number would be branded invalid the moment a
      // chunk fetch failed. Nothing has been judged here, so nothing is said.
      if (!intlTelInput.utils) return setInvalid(false)
      setInvalid(input.value.trim() !== '' && iti.isValidNumber() === false)
    }

    input.addEventListener('blur', check)
    input.addEventListener('input', report)
    input.addEventListener('countrychange', report)

    return () => {
      input.removeEventListener('blur', check)
      input.removeEventListener('input', report)
      input.removeEventListener('countrychange', report)
      itiRef.current = null
      iti.destroy()
    }
  }, [])

  // A value set from outside — a resumed session restoring the funnel store, or
  // a reset form — has to reach the widget, which React cannot do by re-render.
  useEffect(() => {
    const iti = itiRef.current
    if (!iti || value === reportedRef.current) return
    reportedRef.current = value
    withoutUtils(() => iti.setNumber(value))
  }, [value])

  // Following the country of residence is a convenience, not a correction: once
  // there is a number in the box, its prefix is the user's answer, not ours.
  useEffect(() => {
    const iti = itiRef.current
    if (!iti || !country) return
    if (inputRef.current?.value) return
    if (!dialCodeFor(country)) return
    withoutUtils(() => iti.setSelectedCountry(country.toLowerCase() as Iso2))
  }, [country])

  useEffect(() => {
    itiRef.current?.setDisabled(Boolean(disabled))
  }, [disabled])

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            {' '}
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>

      <input
        id={id}
        ref={inputRef}
        type="tel"
        inputMode="tel"
        // `tel-national`, not `tel`: the dial code lives in the selector beside
        // this box, so a filled-in `+66 81 …` would arrive with its prefix
        // doubled.
        autoComplete="tel-national"
        {...(name ? { name } : {})}
        required={required}
        disabled={disabled}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={error || invalid ? errorId : hint ? hintId : undefined}
        className={cn(
          'min-h-[44px] w-full rounded-lg border bg-white px-3 py-2 text-ink',
          'placeholder:text-muted-soft',
          error || invalid ? 'border-danger' : 'border-line-strong',
          'disabled:bg-surface-alt disabled:text-muted',
        )}
      />

      {/*
        The server's message outranks ours: it knows why the CRM refused the
        number, and libphonenumber only knows the shape is wrong.

        This never blocks submit. `phone` is optional in the contract, and
        libphonenumber's ranges lag real carrier allocations — refusing to
        submit on a false negative would be a worse failure than sending a
        number the CRM can judge for itself.
      */}
      {error || invalid ? (
        <p id={errorId} role="alert" className="flex items-start gap-1.5 text-sm text-danger">
          <span aria-hidden="true">⚠</span>
          <span>{error ?? invalidText}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-muted">
          {hint}
        </p>
      ) : null}
    </div>
  )
}
